/**
 * `KeymapProvider` / `useKeymap` (#70) — the single live source of truth for
 * the effective keymap, plus the input-suppression flag.
 *
 * Why this exists: #64/#69 had `InputSurface` resolve the keymap ONCE into a
 * ref at mount, so a rebind couldn't take effect without a page reload. This
 * provider holds the effective keymap in React state; a rebind calls
 * `setBrowserKeymap`, which persists via `savePrefs` (prefs.ts write path) and
 * re-resolves, re-rendering every consumer — the "live refresh".
 *
 * It also owns `inputSuppressed`: while the settings UI is open (in-match
 * overlay) or a capture row is armed, game input must NOT fire. Consumers
 * (`InputSurface`) read this and gate their key handling. This is the
 * load-bearing safety property for in-match rebinding — a key pressed to
 * rebind "Ability 3" must not also cast it.
 *
 * Account tier (#65) later: `setBrowserKeymap` writes the browser tier; when
 * accounts exist a parallel account tier feeds `resolveKeymap` here with no
 * consumer change.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { DEFAULT_KEYMAP } from "./keymap.js";
import type { Keymap } from "./keymap.js";
import { resolveKeymap } from "./resolve.js";
import { serializeKeymap } from "./persist.js";
import { loadKeymap as loadKeymapFromPrefs, savePrefs, loadPrefs } from "../../prefs.js";

/**
 * Persistence seam. Defaults to the `prefs.ts` localStorage boundary;
 * injectable so tests (and any non-localStorage host) can supply a store
 * without depending on a possibly-stubbed global `localStorage`.
 */
export interface KeymapPersistence {
  readonly load: () => Keymap | null;
  readonly save: (keymap: Keymap) => void;
}

/** Default persistence: the per-browser prefs localStorage boundary. */
export const PREFS_KEYMAP_PERSISTENCE: KeymapPersistence = {
  load: () => loadKeymapFromPrefs(),
  save: (keymap) => {
    const current = loadPrefs();
    savePrefs({ ...current, keymap: serializeKeymap(keymap) });
  },
};

export interface KeymapContextValue {
  /** The effective (resolved) keymap for dispatch + display. */
  readonly keymap: Keymap;
  /** Persist a new per-browser keymap and refresh the effective keymap live. */
  readonly setBrowserKeymap: (next: Keymap) => void;
  /** True while game input must be suppressed (settings open / capturing). */
  readonly inputSuppressed: boolean;
  /** Set the suppression flag (settings screen owns this). */
  readonly setInputSuppressed: (v: boolean) => void;
}

const KeymapCtx = createContext<KeymapContextValue | null>(null);

export function KeymapProvider({
  children,
  persistence = PREFS_KEYMAP_PERSISTENCE,
}: {
  readonly children: ReactNode;
  readonly persistence?: KeymapPersistence;
}): JSX.Element {
  // Seed the browser-tier keymap from persistence once; the effective keymap
  // is derived by resolving it against defaults.
  const [browser, setBrowser] = useState<Keymap | null>(() => persistence.load());
  const [inputSuppressed, setInputSuppressed] = useState(false);

  const keymap = useMemo<Keymap>(
    () => resolveKeymap({ browser, defaults: DEFAULT_KEYMAP }),
    [browser],
  );

  const setBrowserKeymap = useCallback(
    (next: Keymap) => {
      // Persist (failures swallowed inside the persistence impl — prefs are
      // convenience state) and refresh the effective keymap live.
      persistence.save(next);
      setBrowser(next);
    },
    [persistence],
  );

  const value = useMemo<KeymapContextValue>(
    () => ({ keymap, setBrowserKeymap, inputSuppressed, setInputSuppressed }),
    [keymap, setBrowserKeymap, inputSuppressed],
  );

  return <KeymapCtx.Provider value={value}>{children}</KeymapCtx.Provider>;
}

export function useKeymap(): KeymapContextValue {
  const v = useContext(KeymapCtx);
  if (v === null) {
    throw new Error("useKeymap: must be used within <KeymapProvider>");
  }
  return v;
}
